import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '../../../lib/format';
import { BRAND, EXAM_TYPE_LABEL } from '@dru/types';
import type { BillablePlant, InvoicePage, InvoiceRow, PaymentSubmissionRow, RateCardRow, RecentPaymentRow } from '@dru/types';
import { formatPaise } from '@dru/types';
import {
  Card, CardHeader, DataTable, EmptyState, ErrorState, SearchSelect, StatCard, StatSkeleton, StatStrip, StatusBadge, Tabs, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import {
  STANDING_LABEL, isPlantBound, periodLabel, plantChoice, selectedPlant, standingOf, type PlantChoice, type Standing,
} from '../lib/billing-view';

const STANDING_TONE: Record<Standing, Tone> = {
  IN_PREPARATION: 'pending', UNPAID: 'dueSoon', PARTIAL: 'dueSoon', PAID: 'covered', VOID: 'neutral',
};

/** What the list asks the server for; a fuller company is told it sees the latest this many. */
const LIST_LIMIT = 200;

/** Unpaid and partly paid first, then everything else; each group newest first (the server's order). */
const PRIORITY: Record<Standing, number> = { UNPAID: 0, PARTIAL: 0, IN_PREPARATION: 1, PAID: 2, VOID: 3 };
const byPriority = (rows: InvoiceRow[]) =>
  rows.map((r, i) => [r, i] as const)
    .sort((a, b) => PRIORITY[standingOf(a[0])] - PRIORITY[standingOf(b[0])] || a[1] - b[1])
    .map(([r]) => r);

/** The invoice list's filters, in the words an accounts team uses. */
type StatusFilter = 'all' | 'open' | 'PAID' | 'VOID';
const FILTERS: Array<{ key: StatusFilter; label: string; match: (s: Standing) => boolean }> = [
  { key: 'open', label: 'To pay', match: (s) => s === 'UNPAID' || s === 'PARTIAL' },
  { key: 'PAID', label: 'Paid', match: (s) => s === 'PAID' },
  { key: 'VOID', label: 'Void', match: (s) => s === 'VOID' },
  { key: 'all', label: 'All', match: () => true },
];

/** Past this many open invoices the per-invoice look-up below is skipped. */
const PENDING_LOOKUP_LIMIT = 25;

/**
 * What has been submitted against each open invoice and is still awaiting
 * Dr Ultimate's verification, by invoice id.
 *
 * The invoice list does not carry it (only the invoice page does), so it is
 * read per open invoice — a plant rarely has more than a handful. It is shown
 * beside the confirmed figures, never folded into them: a submission is a
 * claim, and outstanding only drops once finance confirms it. `null` means
 * "not known", and the screen then says nothing rather than implying zero.
 */
function usePendingByInvoice(open: InvoiceRow[], ready: boolean): Map<string, number> | null {
  const ids = open.map((i) => i.id);
  const key = ids.join(',');
  const skip = !ready || ids.length > PENDING_LOOKUP_LIMIT;
  const q = useQuery<Map<string, number> | null>(async () => {
    if (skip) return null;
    const lists = await Promise.all(ids.map((id) => api
      .get<{ items: PaymentSubmissionRow[] }>(`/invoices/${id}/payment-submissions`)
      .then((r) => [id, r.items.filter((s) => s.status === 'PENDING').reduce((n, s) => n + s.amountPaise, 0)] as const)));
    return new Map(lists);
  }, [key, skip]);
  return q.error ? null : q.data;
}

/**
 * A company's invoices, from the company's side (docs/37 Step 5).
 *
 * One screen for two readers. The Company Admin reads every plant, with a
 * company total and a plant-wise summary, and can narrow to one plant. Plant
 * Accounts reads the plants it is bound to and nothing else — the server
 * filters every read by the binding, so the plant control here is a
 * convenience and never the boundary.
 *
 * Read-only. Money received is recorded by Dr Ultimate finance; nothing on
 * this screen issues, records or reverses anything. Nothing clinical is read.
 */
export function Billing({ tab }: { tab: 'overview' | 'invoices' }) {
  const { principal, permissions } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Billing is its own permission. Without it there is nothing to ask the
  // server for — say so plainly rather than render its refusal as an error.
  const allowed = permissions.has('invoice.read');
  const plants = useQuery<{ items: BillablePlant[] } | null>(
    () => (allowed ? api.get('/invoices/plants') : Promise.resolve(null)), [allowed],
  );
  // The header's plant switcher already narrows every read to one plant on
  // the server (x-view-plant). A second "All plants" picker here would offer
  // a company total the server is not returning — so while the header holds
  // a plant, or the company has only one, this page is that plant's.
  const listed = plants.data?.items ?? [];
  const headerPlant = api.getViewPlant();
  const raw = plantChoice(principal, listed);
  const choice: PlantChoice = raw.mode === 'company' && (headerPlant || raw.options.length === 1)
    ? { mode: 'single', plant: raw.options.find((p) => p.id === headerPlant) ?? raw.options[0]! }
    : raw;
  // A plant-bound login has no header switcher, so its plant is named here.
  const showPlantLabel = choice.mode === 'single' && isPlantBound(principal);
  const plantId = selectedPlant(choice, params.get('plant'));
  const ready = allowed && !plants.loading && !plants.error && choice.mode !== 'none';
  const filter = plantId ? `&plantId=${plantId}` : '';

  // Held until the plants are known, so a plant-bound login does not first
  // fetch "all of mine" and then its one plant.
  const page = useQuery<InvoicePage | null>(
    () => (ready ? api.get(`/invoices?limit=${LIST_LIMIT}${filter}`) : Promise.resolve(null)), [ready, filter],
  );
  const payments = useQuery<{ items: RecentPaymentRow[] } | null>(
    () => (ready ? api.get(`/invoices/payments?limit=5${filter}`) : Promise.resolve(null)), [ready, filter],
  );
  const canReadCards = permissions.has('ratecard.read');
  const cards = useQuery<{ items: RateCardRow[] }>(
    () => (canReadCards ? api.get('/rate-cards') : Promise.resolve({ items: [] })), [canReadCards],
  );

  const choose = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('plant', id); else next.delete('plant');
    setParams(next, { replace: true });
  };
  const suffix = params.get('plant') ? `?plant=${params.get('plant')}` : '';
  const selected = (plants.data?.items ?? []).find((p) => p.id === plantId);
  const openInvoices = (page.data?.items ?? []).filter((i) => i.status === 'ISSUED' && (i.outstandingPaise ?? 0) > 0);
  const pending = usePendingByInvoice(openInvoices, !!page.data && !page.loading);

  if (!allowed) {
    return (
      <div className="space-y-4">
        <PageHeader className="mb-0" title="Billing" />
        <EmptyState title="Billing is not part of your access" hint="Invoices are read by your company administrator and plant accounts team." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Billing"
        hint={choice.mode === 'company'
          ? `Every plant’s invoices: billed, paid (confirmed by ${BRAND.name}), awaiting verification, and outstanding.`
          : `Your plant’s invoices: billed, paid (confirmed by ${BRAND.name}), awaiting verification, and outstanding.`}
        actions={(choice.mode !== 'single' || showPlantLabel) && <PlantControl choice={choice} value={plantId} onChange={choose} />}
      />

      {plants.error && <ErrorState detail={plants.error} onRetry={plants.reload} />}
      {plants.data && choice.mode === 'none' && (
        <EmptyState
          title="No plant to show"
          hint="Your account is not assigned to a plant whose invoices you can read. Ask your company administrator to check your plant assignment."
        />
      )}

      {ready && (
        <>
          <Tabs
            tabs={[{ key: 'overview', label: 'Overview' }, { key: 'invoices', label: 'Invoices' }]}
            active={tab}
            onChange={(k) => navigate(`${k === 'overview' ? '/hr/billing' : '/hr/billing/invoices'}${suffix}`)}
          />
          {page.error && <ErrorState detail={page.error} onRetry={page.reload} />}
          {tab === 'overview' && !page.error && (
            <Overview
              page={page.data} loading={page.loading || !page.data}
              heading={selected ? `${selected.name} · ${selected.code}` : 'Company total (all plants)'}
              companyWide={!plantId && (page.data?.byPlant.length ?? 0) > 1}
              onPlant={choose}
              payments={payments.data?.items ?? []} paymentsLoading={payments.loading}
              cards={canReadCards ? cards.data?.items ?? [] : null}
              invoicesHref={`/hr/billing/invoices${suffix}`}
              pending={pending}
            />
          )}
          {tab === 'invoices' && !page.error && (
            <InvoiceList
              rows={page.data?.items ?? []} loading={page.loading || !page.data} pending={pending}
              showPlant={new Set((page.data?.items ?? []).map((i) => i.plantId ?? i.billToPlantCode)).size > 1}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * The plant control. A company-wide reader chooses among all plants or one;
 * a login bound to several plants switches among exactly those; a login bound
 * to one plant sees its name — a selector with one real choice is a fake one.
 */
function PlantControl({ choice, value, onChange }: {
  choice: PlantChoice; value: string; onChange: (id: string) => void;
}) {
  if (choice.mode === 'none') return null;
  if (choice.mode === 'single') {
    return (
      <div className="rounded border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-700">
        {choice.plant.name} <span className="text-neutral-500">· {choice.plant.code}</span>
      </div>
    );
  }
  return (
    <SearchSelect
      aria-label="Plant"
      value={value}
      onChange={onChange}
      className="w-56"
      placeholder={choice.mode === 'company' ? 'All plants' : 'Choose a plant'}
      searchPlaceholder="Search plants by name or code…"
      options={[
        ...(choice.mode === 'company' ? [{ value: '', label: 'All plants' }] : []),
        ...choice.options.map((p) => ({ value: p.id, label: p.name, hint: p.code })),
      ]}
    />
  );
}

function Overview({ page, loading, heading, companyWide, onPlant, payments, paymentsLoading, cards, invoicesHref, pending }: {
  page: InvoicePage | null; loading: boolean; heading: string; companyWide: boolean;
  onPlant: (id: string) => void;
  payments: RecentPaymentRow[]; paymentsLoading: boolean;
  cards: RateCardRow[] | null; invoicesHref: string;
  pending: Map<string, number> | null;
}) {
  const navigate = useNavigate();
  const t = page?.totals;
  // What still has to be paid comes first, then the latest of the rest: an
  // accounts team opens this screen to find the bill it owes, and five paid
  // invoices above it hide the one that matters.
  const recent = byPriority((page?.items ?? []).filter((i) => i.status !== 'DRAFT')).slice(0, 5);
  const current = cards?.find((c) => c.status === 'PUBLISHED');
  const pendingTotal = pending ? [...pending.values()].reduce((n, p) => n + p, 0) : null;
  const pendingCount = pending ? [...pending.values()].filter((p) => p > 0).length : 0;
  const open = t?.openCount ?? 0;

  return (
    <div className="space-y-4">
      <section className="space-y-2" aria-labelledby="billing-figures">
        <h2 id="billing-figures" className="text-h4 font-semibold text-neutral-900">{heading}</h2>
        {loading ? <StatSkeleton count={4} /> : (
          // Four separate facts, never one merged into another: a payment the
          // company has submitted is shown as awaiting verification and does
          // not reduce Outstanding until Dr Ultimate confirms it.
          <StatStrip className="sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Total billed" value={formatPaise(t?.billedPaise ?? 0)} sub="Issued invoices" />
            <StatCard label="Paid (confirmed)" value={formatPaise(t?.paidPaise ?? 0)} sub={`Verified and recorded by ${BRAND.name}`} />
            <StatCard
              label="Awaiting verification"
              value={pendingTotal === null ? '—' : formatPaise(pendingTotal)}
              valueTone={pendingTotal ? 'pending' : undefined}
              sub={pendingTotal === null
                ? 'Open an invoice to see its submitted payments'
                : pendingTotal > 0
                  ? `Submitted on ${pendingCount} invoice${pendingCount === 1 ? '' : 's'} — not counted as paid yet`
                  : 'None awaiting verification'}
            />
            <StatCard
              label="Outstanding" value={formatPaise(t?.outstandingPaise ?? 0)}
              sub={t?.outstandingPaise
                ? `${open} open invoice${open === 1 ? '' : 's'} · confirmed payments only`
                : 'Nothing outstanding'}
              valueTone={t && t.outstandingPaise > 0 ? 'overdue' : undefined}
            />
          </StatStrip>
        )}
      </section>

      {companyWide && (
        <section className="space-y-2">
          <h2 className="text-h4 font-semibold text-neutral-900">Plant-wise summary</h2>
          <DataTable<InvoicePage['byPlant'][number]>
            rows={page?.byPlant ?? []}
            loading={loading}
            rowKey={(p) => p.plant.id}
            onRowClick={(p) => onPlant(p.plant.id)}
            empty={{ title: 'No invoices yet', hint: `Invoices appear here once ${BRAND.name} issues them.` }}
            columns={[
              { key: 'plant', header: 'Plant', alwaysVisible: true, cell: (p) => (
                <span className="text-left text-sm font-medium text-primary-600">
                  {p.plant.name} <span className="font-normal text-neutral-500">· {p.plant.code}</span>
                </span>
              ) },
              { key: 'billed', header: 'Total billed', align: 'right', cell: (p) => <span className="tabular-nums">{formatPaise(p.billedPaise)}</span> },
              { key: 'paid', header: 'Paid (confirmed)', align: 'right', cell: (p) => <span className="tabular-nums">{formatPaise(p.paidPaise)}</span> },
              { key: 'due', header: 'Outstanding', align: 'right', cell: (p) => (
                <span className={`tabular-nums ${p.outstandingPaise > 0 ? 'font-medium text-overdue-600' : 'text-neutral-500'}`}>{formatPaise(p.outstandingPaise)}</span>
              ) },
              { key: 'open', header: 'Open invoices', align: 'right', cell: (p) => <span className="tabular-nums">{p.openCount ?? 0}</span> },
            ]}
          />
        </section>
      )}

      {/* Side by side only where both tables fit whole; below that they stack
          rather than scrolling their right-hand columns out of view. */}
      <div className="grid gap-4 xl:grid-cols-2">
        <section className="min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-h4 font-semibold text-neutral-900">Open and recent invoices</h2>
            <Link to={invoicesHref} className="py-1 text-small font-medium text-primary-600 hover:text-primary-700">View all invoices →</Link>
          </div>
          <DataTable<InvoiceRow>
            rows={recent}
            loading={loading}
            rowKey={(i) => i.id}
            onRowClick={(i) => navigate(`/hr/billing/${i.id}`)}
            empty={{ title: 'No invoices yet', hint: `Invoices appear here once ${BRAND.name} issues them.` }}
            columns={[
              { key: 'no', header: 'Invoice', alwaysVisible: true, cell: (i) => (
                <div>
                  <Link to={`/hr/billing/${i.id}`} onClick={(e) => e.stopPropagation()} className="whitespace-nowrap font-mono text-small font-medium text-primary-600 hover:text-primary-700">{i.invoiceNumber}</Link>
                  {companyWide && <div className="text-caption text-neutral-500">{i.plant?.code ?? i.billToPlantCode}</div>}
                </div>
              ) },
              { key: 'period', header: 'Period', cell: (i) => <span className="whitespace-nowrap text-small">{periodLabel(i.periodStart, i.periodEnd)}</span> },
              { key: 'amount', header: 'Amount', align: 'right', cell: (i) => <span className="whitespace-nowrap tabular-nums text-sm">{formatPaise(i.totalPaise, i.currency)}</span> },
              { key: 'status', header: 'Status', mobileLabel: false, cell: (i) => <Standing invoice={i} pending={pending?.get(i.id)} /> },
            ]}
          />
        </section>

        <section className="min-w-0 space-y-2">
          <h2 className="text-h4 font-semibold text-neutral-900">Recent confirmed payments</h2>
          <DataTable<RecentPaymentRow>
            rows={payments}
            loading={paymentsLoading}
            rowKey={(p) => p.id}
            empty={{ title: 'No payments confirmed yet', hint: `A payment shows here once ${BRAND.name} has verified and recorded it. Submitted payments are listed on each invoice.` }}
            columns={[
              { key: 'date', header: 'Received', alwaysVisible: true, cell: (p) => <span className="whitespace-nowrap text-small">{formatDate(p.receivedOn)}</span> },
              { key: 'invoice', header: 'Invoice', cell: (p) => (
                <Link to={`/hr/billing/${p.invoice.id}`} className="whitespace-nowrap font-mono text-small text-primary-600 hover:text-primary-700">{p.invoice.invoiceNumber}</Link>
              ) },
              { key: 'ref', header: 'Reference', cell: (p) => <span className="text-small text-neutral-600">{p.reference ?? '—'}{p.reversesId ? ' · reversal' : ''}</span> },
              { key: 'amount', header: 'Amount', align: 'right', cell: (p) => <span className="whitespace-nowrap tabular-nums text-sm">{formatPaise(p.amountPaise, p.invoice.currency)}</span> },
            ]}
          />
        </section>
      </div>

      {cards && (
        <Card>
          <CardHeader
            title="Active rate card"
            hint={current ? current.name : 'No rate card has been published for your company yet.'}
          />
          {current && (
            <ul className="divide-y divide-neutral-100 border-t border-neutral-100 px-5 pb-2">
              {current.lines.map((l) => (
                <li key={l.id} className="flex items-baseline justify-between gap-3 py-1.5 text-small">
                  <span className="text-neutral-700">
                    {l.protocol?.name
                      ?? (l.examinationType ? (EXAM_TYPE_LABEL[l.examinationType] ?? l.examinationType) : 'Default / other examinations')}
                  </span>
                  <span className="shrink-0 tabular-nums text-neutral-900">{formatPaise(l.amountPaise, current.currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * Every invoice, the ones still to pay first. The filters split the loaded
 * list by where each bill stands; the server returns the latest
 * {LIST_LIMIT}, and the screen says so when it is holding that many.
 */
function InvoiceList({ rows, loading, pending, showPlant }: {
  rows: InvoiceRow[]; loading: boolean; pending: Map<string, number> | null; showPlant: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const requested = params.get('status') as StatusFilter | null;
  const active: StatusFilter = FILTERS.some((f) => f.key === requested) ? requested! : 'all';
  const count = (f: (typeof FILTERS)[number]) => rows.filter((r) => f.match(standingOf(r))).length;
  const filter = FILTERS.find((f) => f.key === active)!;
  const shown = byPriority(rows.filter((r) => filter.match(standingOf(r))));
  const choose = (k: StatusFilter) => {
    const next = new URLSearchParams(params);
    if (k === 'all') next.delete('status'); else next.set('status', k);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Show invoices" className="flex gap-2 max-md:-mx-4 max-md:overflow-x-auto max-md:px-4 max-md:pb-1">
        {FILTERS.filter((f) => f.key !== 'VOID' || count(f) > 0).map((f) => {
          const on = f.key === active;
          return (
            <button
              key={f.key} type="button" aria-pressed={on} onClick={() => choose(f.key)}
              className={on
                ? 'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-3.5 text-small font-semibold text-white max-md:h-10'
                : 'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3.5 text-small font-medium text-neutral-700 hover:bg-neutral-50 max-md:h-10'}
            >
              {f.label}
              {/* A full page is not the whole ledger: counts only when everything is loaded. */}
              {!loading && rows.length < LIST_LIMIT && <span className={on ? 'tabular-nums text-white/80' : 'tabular-nums text-neutral-500'}>{count(f)}</span>}
            </button>
          );
        })}
      </div>
      {!loading && rows.length >= LIST_LIMIT && (
        <p className="text-small text-neutral-600">Showing your latest {LIST_LIMIT} invoices.</p>
      )}
      <InvoiceTable
        rows={shown} loading={loading} pending={pending} showPlant={showPlant}
        empty={active === 'open'
          ? { title: 'Nothing to pay', hint: 'Every issued invoice is paid in full.' }
          : rows.length > 0
            ? { title: 'No invoices here', hint: 'Choose another filter to see the rest.' }
            : undefined}
      />
    </div>
  );
}

function InvoiceTable({ rows, loading, pending, showPlant, empty }: {
  rows: InvoiceRow[]; loading: boolean; pending: Map<string, number> | null; showPlant: boolean;
  empty?: { title: string; hint: string };
}) {
  const navigate = useNavigate();
  return (
    <DataTable<InvoiceRow>
      rows={rows}
      loading={loading}
      rowKey={(i) => i.id}
      onRowClick={(i) => navigate(`/hr/billing/${i.id}`)}
      empty={empty ?? { title: 'No invoices yet', hint: `Invoices appear here once ${BRAND.name} issues them.` }}
      columns={[
        { key: 'no', header: 'Invoice No.', alwaysVisible: true, cell: (i) => (
          <div>
            <Link to={`/hr/billing/${i.id}`} onClick={(e) => e.stopPropagation()} className="whitespace-nowrap font-mono text-sm font-medium text-primary-600 hover:text-primary-700">{i.invoiceNumber}</Link>
            {/* Below xl the plant rides under the number instead of taking a column. */}
            {showPlant && (
              <div className="break-words text-caption text-neutral-500 xl:hidden" title={i.plant?.name ?? i.billToPlantName}>
                {i.plant?.code ?? i.billToPlantCode} · {i.plant?.name ?? i.billToPlantName}
              </div>
            )}
          </div>
        ) },
        ...(showPlant
          ? [{ key: 'plant', header: 'Plant', className: 'hidden xl:table-cell', hideOnMobile: true, cell: (i: InvoiceRow) => <PlantCell invoice={i} /> }]
          : []),
        // Below xl, Issue date rides under the period and Paid under the
        // amount, so the money and status columns stay in view instead of
        // scrolling off the right of the card.
        { key: 'period', header: 'Billing period', cell: (i) => (
          <div className="whitespace-nowrap text-small">
            {periodLabel(i.periodStart, i.periodEnd)}
            <div className="text-caption text-neutral-500 xl:hidden">{i.issuedAt ? `Issued ${formatDate(i.issuedAt)}` : 'Not issued'}</div>
          </div>
        ) },
        { key: 'issued', header: 'Issue date', className: 'hidden xl:table-cell', hideOnMobile: true, cell: (i) => <span className="whitespace-nowrap text-small">{i.issuedAt ? formatDate(i.issuedAt) : '—'}</span> },
        { key: 'amount', header: 'Amount', align: 'right', cell: (i) => (
          <div className="whitespace-nowrap">
            <span className="tabular-nums text-sm">{formatPaise(i.totalPaise, i.currency)}</span>
            <div className="text-caption tabular-nums text-neutral-500 xl:hidden">Paid {formatPaise(i.paidPaise ?? 0, i.currency)}</div>
          </div>
        ) },
        { key: 'paid', header: 'Paid (confirmed)', align: 'right', className: 'hidden xl:table-cell', hideOnMobile: true, cell: (i) => <span className="whitespace-nowrap tabular-nums text-sm">{formatPaise(i.paidPaise ?? 0, i.currency)}</span> },
        { key: 'due', header: 'Outstanding', align: 'right', cell: (i) => (
          i.status === 'ISSUED'
            ? <span className={`whitespace-nowrap tabular-nums text-sm ${(i.outstandingPaise ?? 0) > 0 ? 'font-medium text-overdue-600' : 'text-neutral-500'}`}>{formatPaise(i.outstandingPaise ?? 0, i.currency)}</span>
            : <span className="text-small text-neutral-500" aria-label="Not applicable">—</span>
        ) },
        { key: 'status', header: 'Status', mobileLabel: false, cell: (i) => <Standing invoice={i} pending={pending?.get(i.id)} /> },
      ]}
    />
  );
}

/** The plant's name, with its code beneath — long names wrap without pushing the money columns. */
function PlantCell({ invoice: i }: { invoice: InvoiceRow }) {
  return (
    <div className="min-w-[8rem] max-w-[14rem]" title={i.plant?.name ?? i.billToPlantName}>
      <div className="line-clamp-2 text-sm text-neutral-900">{i.plant?.name ?? i.billToPlantName}</div>
      <div className="text-caption text-neutral-500">{i.plant?.code ?? i.billToPlantCode}</div>
    </div>
  );
}

/**
 * Where an invoice stands, from the confirmed ledger — plus, separately, any
 * payment the company has submitted that is still being verified. The second
 * badge is never green and never replaces "Unpaid" or "Partly paid".
 */
function Standing({ invoice, pending }: { invoice: InvoiceRow; pending?: number }) {
  const s = standingOf(invoice);
  return (
    <div className="min-w-[7rem]">
      <StatusBadge tone={STANDING_TONE[s]}>{STANDING_LABEL[s]}</StatusBadge>
      {!!pending && pending > 0 && (
        <div className="mt-0.5 text-caption font-medium text-pending-600">
          {formatPaise(pending, invoice.currency)} submitted, awaiting verification
        </div>
      )}
    </div>
  );
}
